import React, { useLayoutEffect, useRef, useState } from 'react'
import { AdCreative, AssetApprovalStatus, FBAdPreviewFormat } from '@marketing-milk/interfaces'
import { CreativeMedia } from '../../creatives/CreativeCard/CreativeMedia'
import { isDynamicCreative } from '../../campaigns/helpers/campaign.helpers'
import { CreativePreview, getIFrameSource } from 'components/ad-preview'
import { useFetchCreativePreview } from 'hooks/creatives/fetch-creative-preview'
import { StatusPill } from './StatusPill'
import { ReviewNavRail } from './ReviewNavRail'
import {
  PacketModalShell,
  modalGhostButtonStyle,
  modalInputStyle,
  modalPrimaryButtonStyle,
} from '../components/PacketModalShell'
import { dark, radius, status as statusColors, type as typo } from '../tokens'

type Props = {
  creative: AdCreative
  /** Pre-opens the reason form — used when the caller clicked Reject on the card. */
  startInReject?: boolean
  /** Which ad this is out of how many, counting ads only (theme slides aren't numbered). */
  position: { current: number; total: number }
  /** The theme this ad sits under, shown so the slide's framing survives the walk. */
  themeLabel?: string
  hasPrev: boolean
  hasNext: boolean
  onPrev: () => void
  onNext: () => void
  /**
   * Record a verdict. The page owns the mutation for the whole grid, so the modal
   * and the cards can't send different payloads.
   */
  onSubmitVerdict: (verdict: { approved: boolean; notes?: string }) => Promise<unknown>
  submitting: boolean
  /**
   * Employee-only affordances, supplied as capabilities rather than gated on a
   * cohort flag — the page decides who gets what, once, and this modal never
   * learns what a permission is.
   */
  onRemoveApproval?: () => void
  onOpenSource?: () => void
  /** Called after a successful approve/reject — moves to the next ad, or closes if this was the last one. */
  onAdvance: () => void
  onClose: () => void
}

/** Modal padding, and the body height derived from it: overlay inset (16 top+bottom) + this. */
const SHELL_PADDING = 20
const BODY_HEIGHT = `calc(100vh - ${32 + SHELL_PADDING * 2}px)`

/** Natural width of the Facebook mobile-feed render; the preview is scaled, not reflowed. */
const PREVIEW_WIDTH = 360

/**
 * How much of the dialog the ad gets. It reads narrow because the render is a
 * phone, so the copy side keeps the wider half and still comes out ahead.
 */
const PREVIEW_COLUMN_PERCENT = 38

/**
 * The preview is allowed to grow past its natural size — the ad is the thing
 * being judged, and a 360px phone render in a full-height column otherwise
 * leaves most of that column empty. Capped because the iframe's text is
 * rasterized at the scaled size and goes soft well before it runs out of room.
 */
const MAX_PREVIEW_SCALE = 1.45

const ctaLabel = (value?: string) =>
  value
    ? value
        .split('_')
        .map(word => word[0] + word.slice(1).toLowerCase())
        .join(' ')
    : undefined

/**
 * Shrink the ad preview to whatever height is left, instead of letting it decide
 * the dialog's height.
 *
 * It has to be measured rather than set: with a local fallback rendered the
 * preview's height is `auto` (the card's natural height at its fixed width), and
 * that varies by creative — a 4:5 image is far taller than a 1:1 — so no fixed
 * height fits every ad. Scaling also has to re-run after the fact, because the
 * live Facebook iframe resizes itself once it loads.
 */
function useScaleToFit(deps: unknown[]) {
  const outerRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  useLayoutEffect(() => {
    const outer = outerRef.current
    const inner = innerRef.current
    if (!outer || !inner) return undefined

    // `transform` doesn't affect layout size, so `scrollHeight` stays the natural
    // height and re-measuring can't feed back into itself.
    const measure = () => {
      const naturalHeight = inner.scrollHeight
      const naturalWidth = inner.scrollWidth
      if (!naturalHeight || !naturalWidth) return
      setScale(
        Math.min(
          outer.clientHeight / naturalHeight,
          outer.clientWidth / naturalWidth,
          MAX_PREVIEW_SCALE
        )
      )
    }

    measure()
    if (typeof ResizeObserver === 'undefined') return undefined

    const observer = new ResizeObserver(measure)
    observer.observe(outer)
    observer.observe(inner)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return { outerRef, innerRef, scale }
}

/** One label/value row in the metadata panel; renders nothing when value is empty. */
const MetaRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <div
      style={{
        fontFamily: typo.family.sans,
        fontSize: typo.size.eyebrow,
        fontWeight: typo.weight.semibold,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: dark.textFaint,
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    <div
      style={{
        fontFamily: typo.family.sans,
        fontSize: typo.size.body,
        color: dark.text,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  </div>
)

/**
 * The client's full-screen-ish view of one ad: media at a real size beside
 * every piece of copy Facebook will show (primary text, headline,
 * description, CTA, destination), plus the same approve/reject actions as
 * the card. Rejecting reveals an inline reason field here instead of
 * stacking a second modal on top. A successful decision advances to the next
 * ad (slideshow-style) rather than closing — the parent decides what "next"
 * means and closes instead once there isn't one.
 *
 * The dialog is sized by the viewport, not by its contents: a header strip, a
 * body that fills what's left, and an action bar pinned below it. The verdict
 * buttons used to sit at the end of the scrolling copy column, which put the
 * page's primary action below the fold on a laptop whenever the copy ran long.
 */
export function ReviewCreativePreviewModal({
  creative,
  startInReject = false,
  position,
  themeLabel,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onSubmitVerdict,
  submitting,
  onRemoveApproval,
  onOpenSource,
  onAdvance,
  onClose,
}: Props) {
  const [rejecting, setRejecting] = useState(startInReject)
  const [reason, setReason] = useState('')

  // A published ad is live on Facebook; Published already wins the computed status,
  // so the server refuses a verdict on one. Hide the actions rather than offer a
  // button that 400s.
  const canReview = creative.reviewStatus !== AssetApprovalStatus.Published
  const canRemoveApproval =
    !!onRemoveApproval && creative.reviewStatus === AssetApprovalStatus.Approved

  // Dynamic creatives have no single "the ad" to preview live from Facebook —
  // same split the AE editor's `CreativePreviewModal` makes — so they keep the
  // plain asset thumbnail below.
  const isDynamic = isDynamicCreative(creative)

  const { refreshPreview, adCreativePreview, loadingPreview, refreshing, freshPreviewReady } =
    useFetchCreativePreview(creative.id, FBAdPreviewFormat.MOBILE_FEED_STANDARD, {
      // The inline "unavailable" banner from `CreativePreview` replaces the
      // hook's own error toast here, same as the editor's modal.
      suppressErrorToast: true,
    })

  // Force a live refresh on mount so the client is always deciding from the
  // current ad, not a stale hover-prefetched or previously-cached render. The
  // parent remounts this modal (`key={creative.id}`) on every prev/next/advance,
  // so a mount-only effect is enough to refresh per ad.
  useLayoutEffect(() => {
    if (isDynamic) return
    refreshPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDynamic])

  const fbFresh = freshPreviewReady && !loadingPreview && !refreshing
  const iframeSource = fbFresh && adCreativePreview ? getIFrameSource(adCreativePreview) : ''

  // Re-measure whenever the thing being measured can change shape: the live
  // iframe replacing the local fallback, or the reason form opening under it.
  const { outerRef, innerRef, scale } = useScaleToFit([iframeSource, isDynamic, rejecting])

  const destination = creative.destinationURL ?? creative.linkWebsiteURL
  const trimmedReason = reason.trim()

  const handleApprove = async () => {
    await onSubmitVerdict({ approved: true })
    onAdvance()
  }

  const handleSubmitReject = async () => {
    if (!trimmedReason || submitting) return
    await onSubmitVerdict({ approved: false, notes: trimmedReason })
    onAdvance()
  }

  return (
    <>
      <ReviewNavRail hasPrev={hasPrev} hasNext={hasNext} onPrev={onPrev} onNext={onNext} />
      <PacketModalShell
        palette={dark}
        hideHeader
        onClose={onClose}
        maxWidth={1240}
        padding={SHELL_PADDING}
        closeOnBackdropClick
      >
        <div style={{ display: 'flex', height: BODY_HEIGHT, gap: 20 }}>
          {/* The ad owns its column outright, top to bottom. It is the thing being
            judged, so nothing shares its side and nothing above it steals height —
            everything else is a strip down the right. */}
          <div
            ref={outerRef}
            style={{
              flex: `0 0 ${PREVIEW_COLUMN_PERCENT}%`,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: dark.bg,
              border: `1px solid ${dark.hairline}`,
              borderRadius: radius.md,
              padding: isDynamic ? 0 : 12,
              overflow: 'hidden',
            }}
          >
            {isDynamic ? (
              <CreativeMedia creative={creative} className="w-full h-full" objectFit="contain" />
            ) : (
              <div
                ref={innerRef}
                style={{
                  width: PREVIEW_WIDTH,
                  transform: `scale(${scale})`,
                  transformOrigin: 'center center',
                }}
              >
                {/* Same live-Facebook-preview-over-local-fallback surface as the AE
                  editor's `CreativePreviewModal` (`components/ad-preview`'s
                  `CreativePreview`), so the client is looking at the same ad the
                  AE approved from, not just its raw asset.

                  The "preview unavailable" notice is suppressed here and nowhere
                  else: this is the one client-facing surface, and a stranger being
                  asked to approve work doesn't need to be told our Facebook
                  connection is unhappy. Internal surfaces still show it. */}
                <CreativePreview
                  creative={creative}
                  iframeSource={iframeSource}
                  loading={loadingPreview}
                  refreshing={refreshing}
                  onRetry={() => refreshPreview()}
                  width={PREVIEW_WIDTH}
                  frameless
                  hideUnavailableNotice
                />
              </div>
            )}
          </div>

          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}
          >
            {/* Status and position sit over the copy rather than across the dialog,
              so the ad's column starts at the very top. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              <StatusPill creative={creative} />
              <div
                style={{
                  minWidth: 0,
                  fontFamily: typo.family.sans,
                  fontSize: typo.size.eyebrow,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: dark.textFaint,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                Ad {position.current} of {position.total}
                {themeLabel ? ` · ${themeLabel}` : ''}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                style={{
                  marginLeft: 'auto',
                  flexShrink: 0,
                  width: 32,
                  height: 32,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  border: `1px solid ${dark.hairline}`,
                  background: dark.surface,
                  color: dark.textMuted,
                  cursor: 'pointer',
                }}
              >
                <i className="fa fa-times" />
              </button>
            </div>

            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              {creative.primaryText && (
                <MetaRow label="Primary text">{creative.primaryText}</MetaRow>
              )}
              {creative.linkHeadline && <MetaRow label="Headline">{creative.linkHeadline}</MetaRow>}
              {creative.linkDescription && (
                <MetaRow label="Description">{creative.linkDescription}</MetaRow>
              )}
              {creative.justification && (
                <MetaRow label="Why this ad">{creative.justification}</MetaRow>
              )}
              {ctaLabel(creative.callToAction) && (
                <MetaRow label="Call to action">{ctaLabel(creative.callToAction)}</MetaRow>
              )}
              {destination && (
                <MetaRow label="Destination">
                  <a
                    href={destination}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: dark.accent, wordBreak: 'break-all' }}
                  >
                    {creative.linkDisplayURL ?? destination}
                  </a>
                </MetaRow>
              )}

              {/* Copy editing lands here later, and media later still, so a rejection
                about the wrong image still needs somewhere to go in the meantime. */}
              {onOpenSource && (
                <button
                  type="button"
                  onClick={onOpenSource}
                  style={{
                    alignSelf: 'flex-start',
                    padding: 0,
                    border: 'none',
                    background: 'transparent',
                    fontFamily: typo.family.sans,
                    fontSize: typo.size.body,
                    color: dark.accent,
                    cursor: 'pointer',
                  }}
                >
                  Open in Creative Wizard →
                </button>
              )}
            </div>
            {/* Pinned: a verdict is the point of the page, so it never scrolls away. */}
            {(canReview || rejecting) && (
              <div
                style={{
                  flexShrink: 0,
                  borderTop: `1px solid ${dark.hairline}`,
                  paddingTop: 14,
                }}
              >
                {rejecting ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label
                      style={{
                        fontFamily: typo.family.sans,
                        fontSize: typo.size.body,
                        fontWeight: typo.weight.semibold,
                        color: dark.text,
                      }}
                    >
                      Reason *
                    </label>
                    <textarea
                      autoFocus
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                      rows={2}
                      placeholder="What should change?"
                      style={{ ...modalInputStyle(dark), resize: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button
                        type="button"
                        onClick={() => setRejecting(false)}
                        disabled={submitting}
                        style={modalGhostButtonStyle(dark)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSubmitReject}
                        disabled={!trimmedReason || submitting}
                        style={{
                          ...modalPrimaryButtonStyle(dark),
                          flex: '0 0 auto',
                          background: statusColors.danger,
                          borderColor: statusColors.danger,
                          opacity: !trimmedReason || submitting ? 0.6 : 1,
                          cursor: !trimmedReason || submitting ? 'default' : 'pointer',
                        }}
                      >
                        Submit rejection
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={handleApprove}
                      disabled={submitting}
                      style={{
                        ...modalPrimaryButtonStyle(dark),
                        flex: '0 0 auto',
                        padding: '8px 28px',
                        background: statusColors.success,
                        borderColor: statusColors.success,
                        // Dark ink on the bright success fill: white over #22c55e is
                        // barely 2.3:1.
                        color: dark.accentInk,
                        opacity: submitting ? 0.6 : 1,
                      }}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => setRejecting(true)}
                      disabled={submitting}
                      style={{
                        ...modalGhostButtonStyle(dark),
                        color: statusColors.danger,
                        borderColor: statusColors.danger,
                        opacity: submitting ? 0.6 : 1,
                      }}
                    >
                      Reject
                    </button>
                    {/* Subordinate to the two verdicts: undoing a recorded decision is
                      rarer than making one. It had no home at all before the deck's
                      preview action bar retired. */}
                    {canRemoveApproval && (
                      <button
                        type="button"
                        onClick={onRemoveApproval}
                        disabled={submitting}
                        style={{
                          marginLeft: 'auto',
                          padding: 0,
                          border: 'none',
                          background: 'transparent',
                          fontFamily: typo.family.sans,
                          fontSize: typo.size.body,
                          color: dark.textMuted,
                          cursor: submitting ? 'default' : 'pointer',
                          opacity: submitting ? 0.6 : 1,
                        }}
                      >
                        Remove approval
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </PacketModalShell>
    </>
  )
}
